/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    VStack,
    Heading,
    Text,
    List,
    ListItem,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {useLocation} from 'react-router-dom'
import useEinstein from '@salesforce/retail-react-app/app/hooks/use-einstein'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useCategory} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

// Components
import Link from '@salesforce/retail-react-app/app/components/link'

// Utils
import {categoryUrlBuilder} from '../../utils/url'
import {getRootCategoryId} from '../../utils/brandUtils'

// Constants
import {
    MAX_CACHE_AGE_M,
    STALE_WHILE_REVALIDATE_M,
    CAT_MENU_SITEMAP_NAV_SSR_DEPTH
} from '../../constants'

const messages = {
    title: {
        id: 'sitemap.title',
        defaultMessage: 'Sitemap'
    },
    description: {
        id: 'sitemap.description',
        defaultMessage: 'Find all the pages on our website organized in one place.'
    },
    categoriesTitle: {
        id: 'sitemap.categories.title',
        defaultMessage: 'Shop by Category'
    }
}

const Sitemap = () => {
    const intl = useIntl()
    const einstein = useEinstein()
    const {res} = useServerContext()
    const {pathname} = useLocation()
    const {site} = useMultiSite()

    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_M}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_M}`
        )
    }

    useEffect(() => {
        einstein.sendViewPage(pathname)
    }, [pathname, einstein])

    // Get brand root category following the same pattern as _app
    const ROOT_CATEGORY_GARAGE = getRootCategoryId(site.id, 'garage')

    // Fetch categories tree following the same pattern as navigation menu
    const {data: categoriesTree, isLoading: isCategoriesLoading} = useCategory({
        parameters: {
            id: ROOT_CATEGORY_GARAGE,
            levels: CAT_MENU_SITEMAP_NAV_SSR_DEPTH
        }
    })

    // Generate hierarchical category structure with subcategories
    const categoryHierarchy =
        categoriesTree?.categories?.map((category) => ({
            label: category.name,
            href: categoryUrlBuilder(category),
            subcategories:
                category.categories?.map((subCategory) => ({
                    label: subCategory.name,
                    href: categoryUrlBuilder(subCategory)
                })) || []
        })) || []

    const SitemapSection = ({title, links, isLoading}) => (
        <Box as="section" w="full" mb={6}>
            <Heading
                as="h2"
                size="lg"
                mb={4}
                color="black"
                borderBottom="2px solid"
                borderColor="gray.200"
                pb={2}
            >
                {title}
            </Heading>
            <List spacing={2}>
                {isLoading
                    ? // Show skeleton while loading
                      Array.from({length: 4}).map((_, index) => (
                          <ListItem key={`skeleton-${index}`}>
                              <Skeleton height="20px" width="150px" />
                          </ListItem>
                      ))
                    : links.map((link, index) => (
                          <ListItem key={`${link.label}-${index}`}>
                              <Link display="inline-block" w="auto" to={link.href}>
                                  <Text pl={3} fontSize="md" color="black">
                                      {link.label}
                                  </Text>
                              </Link>
                          </ListItem>
                      ))}
            </List>
        </Box>
    )

    const HierarchicalSitemapSection = ({title, categoryHierarchy, isLoading}) => (
        <Box as="section" w="full" mb={6}>
            <Heading
                as="h2"
                size="lg"
                mb={4}
                color="black"
                borderBottom="2px solid"
                borderColor="gray.200"
                pb={2}
            >
                {title}
            </Heading>
            <List spacing={4}>
                {isLoading
                    ? // Show skeleton while loading
                      Array.from({length: 4}).map((_, index) => (
                          <ListItem key={`skeleton-${index}`}>
                              <Skeleton height="20px" width="150px" />
                          </ListItem>
                      ))
                    : categoryHierarchy.map((category, index) => (
                          <ListItem key={`${category.label}-${index}`}>
                              <VStack align="start" spacing={2}>
                                  {/* Main category */}
                                  <Link display="inline-block" w="auto" to={category.href}>
                                      <Text
                                          pl={3}
                                          fontSize="md"
                                          color="black"
                                          fontWeight="semibold"
                                      >
                                          {category.label}
                                      </Text>
                                  </Link>

                                  {/* Subcategories */}
                                  {category.subcategories?.length > 0 && (
                                      <List spacing={1} ml={4}>
                                          {/* "All [Category]" link */}
                                          <ListItem>
                                              <Link
                                                  display="inline-block"
                                                  w="auto"
                                                  to={category.href}
                                              >
                                                  <Text pl={3} fontSize="sm" color="gray.600">
                                                      All {category.label}
                                                  </Text>
                                              </Link>
                                          </ListItem>
                                          {/* Subcategory links */}
                                          {category.subcategories.map((subCategory, subIndex) => (
                                              <ListItem key={`${subCategory.label}-${subIndex}`}>
                                                  <Link
                                                      display="inline-block"
                                                      w="auto"
                                                      to={subCategory.href}
                                                  >
                                                      <Text pl={3} fontSize="sm" color="gray.600">
                                                          {subCategory.label}
                                                      </Text>
                                                  </Link>
                                              </ListItem>
                                          ))}
                                      </List>
                                  )}
                              </VStack>
                          </ListItem>
                      ))}
            </List>
        </Box>
    )

    SitemapSection.propTypes = {
        title: PropTypes.string.isRequired,
        links: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                href: PropTypes.string.isRequired
            })
        ).isRequired,
        isLoading: PropTypes.bool
    }

    HierarchicalSitemapSection.propTypes = {
        title: PropTypes.string.isRequired,
        categoryHierarchy: PropTypes.arrayOf(
            PropTypes.shape({
                label: PropTypes.string.isRequired,
                href: PropTypes.string.isRequired,
                subcategories: PropTypes.arrayOf(
                    PropTypes.shape({
                        label: PropTypes.string.isRequired,
                        href: PropTypes.string.isRequired
                    })
                )
            })
        ).isRequired,
        isLoading: PropTypes.bool
    }

    return (
        <Box data-testid="sitemap-page" layerStyle="page" pb={0}>
            <VStack spacing={8} align="start">
                <Box>
                    <Heading as="h1" size="xl" mb={4}>
                        <FormattedMessage {...messages.title} />
                    </Heading>
                </Box>

                <VStack spacing={6} align="start" w="full">
                    <HierarchicalSitemapSection
                        title={intl.formatMessage(messages.categoriesTitle)}
                        categoryHierarchy={categoryHierarchy}
                        isLoading={isCategoriesLoading}
                    />
                </VStack>
            </VStack>
        </Box>
    )
}

export default Sitemap
